import React from 'react';
import ReactDom from 'react-dom';
import { graphql } from 'gatsby';
import { useIntl } from 'react-intl';

import { ExcludesNull } from 'types/excludesNull';
import { PrismicImage } from 'types/prismicImage';
import { Prismic_Article } from 'types/prismic';

import { metaFragment } from 'graphql/metaFragment';
import { personFragment } from 'graphql/personFragment';
import { articleFragment } from 'graphql/articleFragment';
import { pageFragment } from 'graphql/pageFragment';
import { linkableFragment } from 'graphql/linkableFragment';
import { projectFragment } from 'graphql/projectFragment';
import { articleSlices } from 'graphql/slices';

import { format, formatTime } from 'utils/date';
import { translateTag } from 'utils/intl';
import { linkResolver } from 'utils/linkResolver';
import { getLang } from 'utils/getLangPath';

import { Slices } from 'containers/slices/SliceContainer';

import { Header } from 'components/header/Header';
import { ArrowButton } from 'components/arrow-button/ArrowButton';
import { Progress } from 'components/progress/Progress';
import { ArticleHero } from 'components/article-hero/ArticleHero';
import { Image } from 'components/image/Image';
import { asText } from 'components/rich-text/RichText';
import { ArticleColophon } from 'components/article-colophon/ArticleColophon';
import { Segment } from 'components/segment/Segment';
import { Prismic_Person, Prismic_ArticleMetadata } from '../types/prismic';
import { Seo } from 'components/seo/Seo';
import { EventWrapper } from 'components/event-wrapper/EventWrapper';
import { EventHeader } from 'components/event-wrapper/EventHeader';
import { associationFragment } from '../graphql/associationFragment';

interface IArticleProps {
  data?: {
    prismic?: {
      article?: Prismic_Article | null;
    };
  };
  location: {
    href: string;
  };
}

function Event({ event }: { event: Prismic_Article }) {
  const intl = useIntl();
  const { locale } = intl;

  const title = asText(event?.title ?? []);
  const date = format(event?.published_date ?? null) ?? undefined;
  const time = formatTime(event?.published_date ?? null) ?? undefined;
  const link = event?.event_link ?? undefined;
  const linkText = asText(event.event_link_text ?? []) ?? undefined;
  const lead = event.lead ?? undefined;

  const defaultLinkMessage = intl.formatMessage({
    id: 'events.linkText',
    defaultMessage: 'Lesa meira',
  });

  return (
    <>
      <Seo
        title={asText(event?.seo_title) || asText(event.title)}
        description={asText(event?.seo_description)}
        image={((event?.seo_image ?? null) || event.image) as PrismicImage}
      />

      <Header logo={false}>
        <ArrowButton to={`${getLang(locale)}/vidburdir`}>
          {intl.formatMessage({ id: 'events.back', defaultMessage: 'Aftur í viðburði' })}
        </ArrowButton>
      </Header>

      <EventWrapper
        heading={title}
        header={
          <EventHeader
            date={date}
            time={time}
            link={linkResolver(link)}
            linkText={linkText ?? defaultLinkMessage}
          >
            {asText(lead)}
          </EventHeader>
        }
      >
        <Slices data={event.body ?? []} isColumnLayout></Slices>
      </EventWrapper>
    </>
  );
}

export default function Article({ data, location }: IArticleProps) {
  const article: Prismic_Article | null = data?.prismic?.article ?? null;

  if (!article) {
    return null;
  }

  const intl = useIntl();
  const { locale } = intl;

  const { image = {}, caption = [] } = article;

  const title = asText(article?.title ?? []);

  const renderProgress = () => {
    if (typeof document !== 'undefined') {
      return ReactDom.createPortal(<Progress />, document.body);
    }

    return null;
  };

  const date = format(article.published_date ?? null) ?? undefined;
  const tags = article._meta.tags.map(tag => translateTag(tag, locale));
  const author = asText((article?.author as Prismic_Person)?.name ?? []);
  const metadata = (article.metadata ?? [])
    .map((item: Prismic_ArticleMetadata) => {
      const itemTitle = asText(item.metadata_title ?? []);
      const value = asText(item.metadata_value ?? []);

      if (!itemTitle || !value) {
        return null;
      }

      return { title: itemTitle, value };
    })
    .filter((Boolean as any) as ExcludesNull);

  if (article && article.type === 'event') {
    return <Event event={article} />;
  }

  return (
    <>
      <Seo
        title={asText(article?.seo_title) || asText(article.title)}
        description={asText(article?.seo_description)}
        image={(article?.seo_image ?? article.image ?? null) as PrismicImage}
      />

      <Header logo={false}>
        <ArrowButton to={`${getLang(locale)}/ha-frettir`}>
          {intl.formatMessage({ id: 'articles.back', defaultMessage: 'Aftur í HA/fréttir' })}
        </ArrowButton>
      </Header>

      <ArticleHero heading={title} date={format(article?.published_date, locale) ?? undefined}>
        {article.image !== null && (
          <Image
            url={article?.image?.url}
            width={image?.dimensions?.width}
            height={image?.dimensions?.height}
            alt={image?.alt}
            caption={caption ? asText(caption) : undefined}
            setRatio={true}
          />
        )}
      </ArticleHero>
      <Slices data={article.body ?? []}></Slices>

      <Segment isColumnLayout={false}>
        <ArticleColophon
          date={date}
          author={author}
          metadata={metadata}
          tags={tags}
          location={location}
        />
      </Segment>

      {renderProgress()}
    </>
  );
}

export const query = graphql`
  query Article($uid: String!, $lang: String = "is") {
    prismic {
      article(uid: $uid, lang: $lang) {
        _meta {
          ...meta
        }
        title
        type
        image
        caption
        published_date
        lead
        author {
          ...person
        }
        event_link {
          ...linkable
        }
        event_link_text
        event_venue
        seo_title
        seo_description
        seo_image
        metadata {
          metadata_title
          metadata_value
        }
        body {
          ...articleSlices
        }
      }
    }
  }
`;

Article.query = query;
Article.fragments = [
  metaFragment,
  personFragment,
  linkableFragment,
  articleSlices,
  articleFragment,
  pageFragment,
  projectFragment,
  associationFragment,
];
